// =============================================================================
// Inbound Deliveries — the kitchen's "log + photo-confirm what just arrived"
// tile, lifted out of the retired kitchen app (src/boh-kitchen.jsx
// InboundDeliveries + its helpers, 20 Sep 2026) so it renders inside the ONE
// Trafford site app now the kitchen (and its storeroom) is on the premises.
//
// OWN-PREMISES mode — what changed in the lift (everything else is verbatim):
//   * ONE target: the running list ("what's arriving") is this site's items
//     marked ORDERED or flagged Out of Stock / Running Low, exactly the
//     kitchen's board but for `site.key` alone; custom one-offs are tagged
//     `site.key` too. The kitchen's fan-out over ["tc","kitchen"] is gone.
//   * Stock lines post `backInStock` (replenish: clears the Low / Out-of-Stock
//     flag and the reorder, leaves any open till request for the Collect tap to
//     close). The retired single-site branch posted `stockDelivered`, which was
//     "the kitchen van arrived at the kiosk" and closed the request — wrong for a
//     storeroom receipt. Custom one-offs still post `customDeliveryReceived`.
//   * No device-GPS capture and no lat/lng on the POST (see `submit`).
//   * The kitchen's `site === "kitchen"` folds are kept verbatim but are inert:
//     nothing here ever posts "kitchen" any more.
//   * Self-contained: its own B, the six style keys it uses (byte-identical to
//     the FOH's), and no CSS injection — the only keyframe it uses (`spin`)
//     already ships in index.html.
//
// Backend + gates are unchanged: receiveInboundBatch / backInStock /
// customDeliveryReceived (BOH · FOH · Admin on the Apps Script matrix), the
// Supabase photo + evidence writes behind DUAL_RUN_HOSTS, and the running-list
// read through stockReader behind STOCK_READ_HOSTS — all already listing
// trafford.eatrolled.com.
// =============================================================================
import React from "react";
import { getActiveSite } from "../config/sites.js";
import * as stockSync from "./stockSync.js"; // Phase 2 dual-run shadow + the write-drop gate (ships EMPTY)
import * as stockReader from "./stockReader.js"; // running list read from Supabase behind STOCK_READ_HOSTS
import * as deliveriesSync from "./deliveriesSync.js"; // delivery photo + evidence row -> Supabase (best-effort / awaited on a dropped host)
import * as notificationsSync from "./notificationsSync.js"; // a replenish tap closes the item's reorder alert
const { useState, useEffect, useMemo, useRef } = React;

// The deployment's site (the FOH bundle resolves it from the hostname). Used by
// the copied write helpers exactly as the kitchen app used its own global.
const SITE = getActiveSite();

const B = {nori:"#00392C",wasabi:"#D4FB6F",kewpie:"#FEFAF0",rice:"#FFFFFF",coral:"#FF7A5C",sunshine:"#F1C544",eucalyptus:"#6FAF8F"};

// The subset of the kitchen app's S that this tile uses, verbatim.
const S = {
  content:{padding:"0 20px 24px",maxWidth:640,width:"100%",margin:"0 auto"},
  sectionCard:{background:"rgba(212,251,111,0.04)",border:"1px solid rgba(212,251,111,0.1)",borderRadius:14,padding:"14px 16px",marginBottom:16},
  secLabel:{fontFamily:"gelica,serif",fontSize:"1.1rem",fontWeight:700,color:B.wasabi,marginBottom:2},secDesc:{fontSize:"0.8rem",color:B.kewpie,opacity:0.5,lineHeight:1.5},
  input:{width:"100%",padding:"10px 12px",background:"rgba(254,250,240,0.05)",border:"1px solid rgba(212,251,111,0.12)",borderRadius:10,color:B.kewpie,fontFamily:"Manrope,sans-serif",fontSize:"0.9rem",outline:"none",boxSizing:"border-box"},
  submitBtn:{width:"100%",padding:14,background:B.wasabi,color:B.nori,fontFamily:"Manrope,sans-serif",fontSize:"0.9rem",fontWeight:700,border:"none",borderRadius:14,cursor:"pointer",letterSpacing:"0.02em"},
};

function CatalogThumb({item, imageUrl, size, onZoom, marginRight, showPlaceholder}){
  const px = size || 48;
  const wrap = {width:px,height:px,borderRadius:8,background:"rgba(254,250,240,0.06)",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",marginRight:marginRight||0};
  if (!imageUrl) {
    if (!showPlaceholder) return null;
    return <div style={wrap}><span style={{fontSize:Math.round(px*0.42),opacity:0.4}}>📦</span></div>;
  }
  const open = (e)=>{ if(e){e.stopPropagation(); e.preventDefault();} onZoom && onZoom({item, imageUrl}); };
  return (
    <div role="button" tabIndex={0} aria-label={"View "+(item||"item")+" image"}
      onClick={open}
      onKeyDown={e=>{ if(e.key==='Enter'||e.key===' '){ open(e); } }}
      style={{...wrap,cursor:"zoom-in"}}>
      <img src={imageUrl} alt={item||""} draggable={false}
        style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}
        onError={e=>{
          const p = e.currentTarget.parentNode;
          if(!p)return;
          p.innerHTML = '<span style="font-size:'+Math.round(px*0.42)+'px;opacity:0.4">📦</span>';
        }}/>
    </div>
  );
}

// Catalog image lightbox — header strip + dark backdrop + full-size image
// + close ×. Tap backdrop to close, tap image does nothing. zIndex 700
// keeps it above any in-flight modal underneath.
function CatalogThumbLightbox({item, imageUrl, onClose}){
  if (!imageUrl) return null;
  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{position:"fixed",inset:0,background:"rgba(0,28,22,0.88)",zIndex:700,display:"flex",alignItems:"center",justifyContent:"center",padding:24,backdropFilter:"blur(4px)"}}>
      <div onClick={e=>e.stopPropagation()}
        style={{maxWidth:720,width:"100%",maxHeight:"calc(100dvh - 48px)",background:"#fff",borderRadius:14,overflow:"hidden",display:"flex",flexDirection:"column",boxShadow:"0 22px 48px rgba(0,0,0,0.5)"}}>
        <div style={{padding:"14px 18px",background:B.nori,display:"flex",alignItems:"center",justifyContent:"space-between",gap:14,borderBottom:"1px solid rgba(0,57,44,0.15)"}}>
          <div style={{minWidth:0,flex:1}}>
            <div style={{fontSize:11,fontWeight:800,letterSpacing:"0.08em",textTransform:"uppercase",color:"#C8EAD8"}}>📦 Catalogue item</div>
            <div style={{fontFamily:"gelica,serif",fontSize:18,fontWeight:700,color:B.wasabi,marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{item||""}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            style={{width:32,height:32,borderRadius:"50%",background:"rgba(0,0,0,0.3)",color:B.wasabi,border:"none",cursor:"pointer",fontSize:16,fontWeight:700,lineHeight:1}}>×</button>
        </div>
        <div style={{flex:1,minHeight:0,background:"#0a0a0a",display:"flex",alignItems:"center",justifyContent:"center"}}>
          <img src={imageUrl} alt={item||""} draggable={false}
            style={{maxWidth:"100%",maxHeight:"calc(100dvh - 160px)",display:"block"}}
            onError={ev=>{ev.target.style.display="none";ev.target.parentNode.innerHTML='<div style="color:#fff;padding:40px;text-align:center;font-size:13px;opacity:0.7">Image could not load.</div>';}}/>
        </div>
      </div>
    </div>
  );
}

const KITCHEN_GEOFENCED_ACTIONS = ["stockDelivered","stockOutOfStock","stockRunningLow","backInStock"];
function shadowKitchenWrite({ action, site, item, staffPin, supplierRef = null, eta = null, note = null, photoPath = null, qty = null }) {
  try {
    if (!action || !item || !staffPin) return;
    // The kitchen tile serves multiple kiosks and tags its own one-offs "kitchen";
    // that's this deployment's site (mcr) in Supabase terms.
    const siteKey = (site && site !== "kitchen") ? site : SITE.key;
    // Call the recorder DIRECTLY at the tap: it enqueues the row synchronously
    // (durable BEFORE the login) then verifies the pin + stamps attribution and
    // drains. It returns the resolved pass, which we reuse for the reorder alert so
    // there's a single login per tap.
    stockSync.recordKitchen({ siteKey, item, action, supplierRef, eta, note, photoPath, qty, staffPin })
      .then((res) => {
        // The same tap also raises a reorder alert (running low / out of stock),
        // or closes one (delivered / back in stock) — see lib/notificationsSync.js.
        // Fired BESIDE the stock shadow: a stock_activity hiccup must not swallow the
        // alert, and an alert hiccup must not touch the stock row. It swallows its own
        // errors and never throws. Nothing sends an email — the alert queues and
        // waits. One-line kill: NOTIFY_SYNC_ENABLED in lib/notificationsSync.js.
        try { notificationsSync.recordReorderSignal({ siteKey, item, action, pass: res && res.pass }); } catch (_) {}
      })
      .catch((e) => { try { console.warn("[stock-sync] kitchen shadow failed:", e?.message || e); } catch (_) {} });
  } catch (e) { /* best-effort: never break the live kitchen path */ }
}

function postKitchenAction(backend, body, geofenced){
  // Phase 2 dual-run: shadow this kitchen write into Supabase (best-effort). Every
  // flag/delivery event in the kitchen funnels through here, incl. cross-site
  // storeroom propagation, so one mirror catches them all. photo_path now carries
  // the SAME photo URL the sheet row gets (an absolute Drive URL during dual-run;
  // the reader passes absolute URLs through) — a photo-confirmed delivery must
  // clear the item's flag on BOTH sides, and the Supabase walker can only apply
  // that rule when it can see the photo (photo_path was null on all 205 delivered
  // rows to 17 Jul, so flagged items lingered forever on the Supabase side).
  try { shadowKitchenWrite({ action: body && body.action, site: body && body.site, item: body && body.item, staffPin: body && body.staffPin, supplierRef: body && body.supplierRef, eta: body && body.eta, note: body && body.note, photoPath: (body && body.photoUrl) || null, qty: (body && body.qty != null) ? body.qty : null }); } catch(_){}
  // Write-drop gate (STOCK_WRITE_DROP_HOSTS — ships EMPTY). ON -> the awaited
  // Supabase landing IS the record and the Apps Script POST is SKIPPED; the shadow
  // + reorder signal above still fire exactly once with the SAME pass. The kitchen
  // idem_key has no per-tap nonce, so shadowKitchenWrite's enqueue and this awaited
  // write COLLAPSE onto ONE row (a 23505 = landed) — no double write. A landing
  // failure returns the same {ok:false,code,message} shape the callers resolve on,
  // so the geofence/error UI path is unchanged. OFF -> the fetch below, untouched.
  // Lane BP (13 Sep 2026): the kitchen offsite geofence flag now rides the ON path
  // too (closes DECISIONS_NEEDED BE-4). The device fix a caller already puts on a
  // GEOFENCED body (lat/lng/accuracy — the same three fields the Sheet's
  // computeKitchenOffsite_ reads) is handed to the row as device_lat/lng/accuracy_m
  // and the DATABASE evaluates + stamps offsite / offsite_distance_m (migration
  // 1300, fence config ships empty = no flag, admin never flagged). A non-geofenced
  // action (customDeliveryReceived) sends no fix, exactly as the Sheet never flags it.
  // The optional qty (the Inbound Deliveries tile) rides the same entry.
  if (stockSync.stockWriteDropEnabled()) {
    var _dropSite = (body && body.site && body.site !== "kitchen") ? body.site : SITE.key;
    var _dropArgs = {
      siteKey: _dropSite, item: body && body.item, action: body && body.action,
      supplierRef: (body && body.supplierRef) || null, eta: (body && body.eta) || null,
      note: (body && body.note) || null, photoPath: (body && body.photoUrl) || null,
      qty: (body && body.qty != null) ? body.qty : null,
      staffPin: body && body.staffPin,
    };
    if (geofenced && body && body.lat != null && body.lng != null) {
      _dropArgs.deviceLat = body.lat;
      _dropArgs.deviceLng = body.lng;
      _dropArgs.deviceAccuracyM = (body.accuracy != null) ? body.accuracy : null;
    }
    return stockSync.stockWriteAuthoritative({ kind: "kitchen", args: _dropArgs }).then(function(res){
      return (res && res.ok)
        ? { ok: true }
        : { ok: false, code: "supabase", message: (res && res.errMsg) || "That didn't save — try again." };
    });
  }
  return new Promise(function(resolve){
    try {
      fetch(backend, {method:"POST", headers:{"Content-Type":"text/plain;charset=utf-8"}, body: JSON.stringify(body)})
        .then(function(r){
          if (!r.ok) return {ok: false, code: "HTTP_"+r.status, message: "Backend returned HTTP " + r.status + "."};
          return r.json().then(function(d){ return {ok: true, data: d}; });
        })
        .then(function(out){
          if (!out || out.ok === false) {
            resolve(out || {ok: false, code: "UNKNOWN", message: "Action failed."});
            return;
          }
          var d = out.data || {};
          // Apps Script handlers return {status:'error', code, message} on
          // failure and {status:'success'} (or a payload) on success. Any
          // explicit error → surface to caller; everything else → ok.
          if (d.status === "error" || d.code === "OUTSIDE_KITCHEN_GEOFENCE") {
            resolve({ok: false, code: d.code || "BACKEND_ERROR", message: d.message || "Backend rejected the action."});
          } else {
            resolve({ok: true, data: d});
          }
        })
        .catch(function(){ resolve({ok: false, code: "NETWORK", message: "Couldn't reach the kitchen backend. Try again."}); });
    } catch(_){
      resolve({ok: false, code: "NETWORK", message: "Couldn't reach the kitchen backend. Try again."});
    }
  });
}

function kitchenActionErrorMessage(res, itemLabel){
  if (!res || res.ok) return "";
  var label = itemLabel ? "“" + itemLabel + "”: " : "";
  if (res.code === "OUTSIDE_KITCHEN_GEOFENCE") return label + (res.message || "This action can only be performed at the storeroom.");
  if (res.code === "NETWORK") return label + "Couldn't reach the server. Try again.";
  if (res.code && res.code.indexOf("HTTP_") === 0) return label + "Backend error (" + res.code + "). Try again.";
  return label + (res.message || "Action failed. Try again.");
}

function InboundDeliveries({backend,requestPin,stockCatalog,site}){
  // Per-site running list — fetched on mount + refreshed every 60s while
  // mounted. Mirrors the FOH Collect tab's data source so the items the
  // staff are matching against are exactly what's currently outstanding.
  //
  // 2026-05-26: stale-while-revalidate cache. The read is now Supabase on a
  // flipped host (see loadRunningList; the old Apps Script round-trip was 2–5s
  // per site on the kitchen tablets) but still not instant, so the SWR seed
  // stands: the previous "Loading running list…" blank-state on every mount was
  // killing efficiency. Seed from localStorage so the list paints instantly,
  // then refresh in the background. Mirrors the XS_CACHE_KEY pattern in
  // StockRequestReceiver.
  const RUNNING_LIST_CACHE_KEY = (site && site.key)
    ? "rolled.inbound.runningList::" + site.key
    : "rolled.kitchen.inbound.runningList";
  const seedRunningListFromCache = () => {
    try {
      const c = localStorage.getItem(RUNNING_LIST_CACHE_KEY);
      if (c) { const parsed = JSON.parse(c); if (Array.isArray(parsed)) return parsed; }
    } catch (e) {}
    return null;
  };
  const cachedSeed = seedRunningListFromCache();
  const [runningList,setRunningList] = useState(cachedSeed || []);
  // Spinner only shows on truly cold start (no cache entry at all). A
  // cached empty array is a meaningful answer — paint the empty state
  // immediately, refresh in the background, no loading flash.
  const [loadingList,setLoadingList] = useState(cachedSeed === null);
  const [listErr,setListErr] = useState(null);
  // Holds the freshest loadRunningList closure for the 60s poll (see below).
  const loadListRef = useRef(null);

  // Recent inbound deliveries — populated optimistically as staff log new
  // deliveries this session. Persisted history lives in admin Stock Activity;
  // this is just a session-local strip so staff can confirm "yes, I just
  // logged that" without flipping tabs.
  const [recentDeliveries,setRecentDeliveries] = useState([]);

  // MULTI-PHOTO (2026-09-10): a delivery can have SEVERAL photos (different
  // pallets/boxes). Each entry: {id, dataUrl, mime, filename, capturedAt,
  // gpsLat, gpsLng, exifPresent, source}. EXIF/source resolved per photo
  // (2026-05-12 3-signal resolver: EXIF DateTimeOriginal → Samsung filename
  // pattern → file.lastModified). The first photo is the primary (shown as the
  // per-item thumbnail + used for the kitchen geofence); the rest ride along.
  const [photos,setPhotos] = useState([]);
  const [note,setNote] = useState("");
  const [submitting,setSubmitting] = useState(false);
  const [uploadStatus,setUploadStatus] = useState("");   // progress line while photos upload
  // Idempotency token for the batch write — stable across retries (internal +
  // a manual re-tap) so a flaky-/exec false-failure can be re-sent without
  // double-logging; the backend dedups per (token,item). Cleared on a clean log.
  const clientTokenRef = useRef("");
  const [error,setError] = useState("");
  const [search,setSearch] = useState("");
  const [customText,setCustomText] = useState("");
  const [tab,setTab] = useState("running"); // "running" | "custom"
  const [photoLightbox,setPhotoLightbox] = useState(null);
  // Tap-to-enlarge for catalogue thumbnails on the running list.
  const [thumbZoom,setThumbZoom] = useState(null);
  // "Did you mean…" modal when the typed custom text clashes with a catalogue
  // SKU. Prevents one-off entries like Iraj's "Rolled stickers" (2026-05-26)
  // bypassing the running list. Mirrors the FOH Stock Requests matcher.
  const [customBlock,setCustomBlock] = useState(null);

  // ---- BULK RECEIVE (2026-09-09) --------------------------------------
  // The tile logs a whole delivery in ONE pass: tick many items into a cart
  // (optional per-item count), take ONE photo of the drop, submit once → a
  // single receiveInboundBatch POST. Replaces the old one-item-at-a-time flow
  // (own photo + own PIN + two writes PER item) that made a big drop — like
  // Sonam's 8-line sweets delivery on 2026-09-08 — take minutes at the tablet.
  // Cart entries: {key,item,site,kind:"stock"|"custom",type,qty|null,row}.
  const [cart,setCart] = useState([]);
  const [stage,setStage] = useState("build");   // "build" (pick items) | "review" (photo + confirm)
  const cartKey = (kind,item,site) => kind+"|"+String(item||"").toLowerCase()+"|"+(site||"");
  const inCart = (kind,item,site) => cart.some(c => c.key === cartKey(kind,item,site));
  const addToCart = (entry) => {
    const key = cartKey(entry.kind, entry.item, entry.site);
    // Count defaults to 1 (the stepper shows immediately — JJ 2026-09-10); staff
    // bump it up for multi-packs, or leave it if the exact count doesn't matter.
    setCart(prev => prev.some(c=>c.key===key) ? prev : [...prev, {...entry, key, qty: entry.qty ?? 1}]);
  };
  const removeFromCart = (key) => setCart(prev => prev.filter(c => c.key !== key));
  // Count stepper: clamped to [1, 999] — never below 1 (remove the row instead).
  const bumpQty = (key, d) => setCart(prev => prev.map(c =>
    c.key === key ? {...c, qty: Math.max(1, Math.min(999, (c.qty || 1) + d))} : c
  ));

  // Inbound = deliveries arriving AT this site from suppliers. Kiosk stock
  // requests are not inbound deliveries for the kitchen — they're handed off
  // via the driver flow + tracked in the Stock Requests tile (the kitchen
  // staging board). When `site` prop is supplied (Meadowhall mount), fetch
  // that site's running list; in kitchen mode, scope to kitchen only.
  // Either way: filter out closed rows so the picker shows only currently-
  // open items expected at this site.
  const loadRunningList = () => {
    // Background refresh — don't touch loadingList. The spinner is only
    // for the truly-first-ever-load case, set during useState init from
    // the cache miss. Once a fetch completes (success OR empty), the list
    // is "loaded" forever for this component lifetime.
    setListErr(null);
    // Kitchen mode (no `site` prop): the Inbound running list is the
    // "what's arriving" board. Show everything the kitchen is expecting to
    // replenish from a supplier — items marked ORDERED or flagged Out of
    // Stock / Running Low — across BOTH kiosks + the kitchen storeroom.
    // (2026-06-08: was site=kitchen open-requests only, so reordered kiosk
    // items like Menu Flyers never appeared here to be received.) The
    // Meadowhall mount keeps its single-site open-request behaviour.
    // OWN-PREMISES mode (the kitchen moved into the Trafford unit, 24 Sep 2026):
    // the storeroom is on site, so the "what's arriving" board is THIS site's
    // items marked ORDERED or flagged Out of Stock / Running Low — the kitchen's
    // filter with a single target. (The retired single-site branch listed every
    // open request: that was a kiosk receiving the kitchen van, not a storeroom.)
    const targets = [site.key];
    const awaitingReplenish = (x) => x.ordered === true || x.flag === "out_of_stock" || x.flag === "running_low";
    const listFilter = awaitingReplenish;
    // READ FLIP (2026-09-09) — this was the LAST screen still fetching the raw
    // Apps Script getStockRequests feed (2–5s per site; the kitchen mount does
    // TWO, so the Inbound list opened cold behind ~4–10s while every other
    // stock screen already read Supabase after the 31 Aug estate flip). Route it
    // through the same live reader (CC-ordered overlay + sheet fallback baked in)
    // when the host is flipped AND the active catalog has loaded — a null catalog
    // makes the reader fail OPEN (emits deactivated items), so stay on the
    // server-filtered sheet until stockCatalog populates. Field-for-field
    // compatible: the reader is a faithful port of the feed, so listFilter
    // (ordered/flag/closed) applies unchanged. Mirrors StockRequestReceiver +
    // the FOH Collect tab. Revert = the flip lives in stockReader.STOCK_READ_HOSTS.
    const activeNames = (() => {
      const n = (stockCatalog || []).filter(c => c && c.name && c.status !== "inactive").map(c => String(c.name).toLowerCase());
      return n.length ? new Set(n) : null;
    })();
    const flipped = stockReader.stockReadEnabled() && activeNames !== null;
    Promise.all(targets.map(s => {
      const sheetUrl = backend + "?action=getStockRequests&site=" + s;
      return (flipped
        ? stockReader.fetchStockRequests(s, { sheetUrl, activeItemNames: activeNames })
        : fetch(sheetUrl, {redirect:"follow", credentials:"omit"}).then(r => r.ok ? r.json() : {requests:[]}))
        .then(d => ((d && d.requests) || []).filter(listFilter).map(x => ({...x, site:s})))
        .catch(() => []);
    })).then(results => {
      const flat = results.flat();
      setRunningList(flat);
      setLoadingList(false);
      // Persist for next mount — SWR seed.
      try { localStorage.setItem(RUNNING_LIST_CACHE_KEY, JSON.stringify(flat)); } catch (e) {}
    }).catch(e => {
      setListErr(e.message || "Failed to load running list");
      setLoadingList(false);
    });
  };
  // Poll through a ref (reassigned every render) so the 60s refresh always runs
  // the LATEST closure — since the read now depends on the stockCatalog prop for
  // its active-name filter, a stale closure could otherwise pin the very first
  // render's (possibly empty) catalog for the tile's whole life and never engage
  // the Supabase flip. Mirrors StockRequestReceiver (loadRef) + xsLoadAll (xsLoadRef).
  loadListRef.current = loadRunningList;
  useEffect(()=>{ loadListRef.current(); const t=setInterval(()=>{ loadListRef.current && loadListRef.current(); },60000); return ()=>clearInterval(t); },[]);

  const filteredList = useMemo(()=>{
    const q = search.trim().toLowerCase();
    if (!q) return runningList;
    return runningList.filter(r => (r.item||"").toLowerCase().includes(q) || (r.requestedBy||"").toLowerCase().includes(q));
  },[runningList, search]);

  // Source param ('camera' | 'library') comes from which input fired — more
  // reliable than time-gap heuristic which mis-classified library picks as
  // 'camera' when Samsung's picker reset file.lastModified to now on copy.
  // Resolve one file's EXIF/source and APPEND it to the photo set (multi-photo).
  const handleFile = async (file, source) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { setError("Photo too large — keep it under 6MB"); return; }
    setError("");
    const mime = file.type || "image/jpeg";
    const src = (source === "camera" || source === "library") ? source : "unknown";
    let resolvedTs = null, hasExifEvidence = false, gLat = null, gLng = null;
    try {
      if (window.exifr && typeof window.exifr.parse === "function") {
        const meta = await window.exifr.parse(file, { tiff: true, exif: true, gps: true, ifd0: true });
        if (meta) {
          const dt = meta.DateTimeOriginal || meta.CreateDate;
          if (dt) { resolvedTs = new Date(dt).getTime(); hasExifEvidence = true; }
          if (typeof meta.latitude === "number") { gLat = meta.latitude; hasExifEvidence = true; }
          if (typeof meta.longitude === "number") gLng = meta.longitude;
        }
      }
    } catch (e) { /* non-fatal */ }
    if (!resolvedTs && file.name) {
      const m = file.name.match(/(\d{4})(\d{2})(\d{2})[_-]?(\d{2})(\d{2})(\d{2})/);
      if (m) {
        const ts = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6])).getTime();
        if (!isNaN(ts) && ts > 0) resolvedTs = ts;
      }
    }
    if (!resolvedTs && file.lastModified) resolvedTs = file.lastModified;
    const capturedAt = resolvedTs ? new Date(resolvedTs).toISOString() : null;
    const reader = new FileReader();
    reader.onload = () => {
      setPhotos(prev => [...prev, {
        id: "p" + Date.now() + "-" + prev.length,
        dataUrl: String(reader.result || ""),
        mime, filename: file.name || "delivery.jpg",
        capturedAt, gpsLat: gLat, gpsLng: gLng, exifPresent: hasExifEvidence, source: src
      }]);
    };
    reader.onerror = () => setError("Could not read file");
    reader.readAsDataURL(file);
  };
  const removePhoto = (id) => setPhotos(prev => prev.filter(p => p.id !== id));

  // Full clear — after a successful batch log. (Stage-2 "Back" keeps the cart
  // and photo and just flips stage to "build".)
  const reset = () => {
    setCart([]); setStage("build");
    setPhotos([]); setNote("");
    setSearch(""); setCustomText(""); setSubmitting(false); setUploadStatus(""); setError("");
    clientTokenRef.current = "";   // fresh idempotency token for the next delivery
  };

  // Label for the shared photo upload + the recent-deliveries strip: the item
  // name for a single-line drop, else an "N-item delivery" summary.
  const batchLabel = cart.length === 1 ? cart[0].item : (cart.length + "-item delivery");

  const canSubmit = photos.length > 0 && cart.length > 0 && !submitting;

  // Catalog-match guard for the Custom tab. Mirrors the FOH Stock Requests
  // matcher (Trafford/Arndale index.html → findCatalogMatches_). Scoring:
  //   - distinctive token hits (×1.5 each) so "rolled stickers" hits Rolled
  //     Stickers even in a verbose note;
  //   - full-string / reverse-substring / subsequence boosts;
  //   - threshold ≥1.0 to drop single-shared-word noise.
  //   - off-catalogue guard: if the typed text contains a distinctive token
  //     that appears in NO catalogue item (e.g. "slushie machine" → "machine"
  //     isn't in any SKU), only allow matches with a full-substring or
  //     codeName match. Stops single-shared-token false positives like
  //     "Slushie machine" surfacing Slushie Mix (JJ 2026-05-26).
  // Iraj logged "Rolled stickers" as +CUSTOM INBOUND on 2026-05-26 when the
  // SKU is in the catalogue — this stops that path.
  // Pre-compute the union of distinctive tokens across the whole active
  // catalogue so the off-catalogue check is O(1) per typed token.
  const catalogDistinctiveTokens = useMemo(() => {
    const norm = s => String(s||"").toLowerCase().replace(/[^a-z0-9 ]+/g," ").replace(/\s+/g," ").trim();
    const stem = w => (w && w.length>3 && w.endsWith("s")) ? w.slice(0,-1) : w;
    const STOP = new Set([
      "the","a","an","of","for","in","on","at","by","with","to","from","and","or","is","are","be","not","this","that","i","we","you","they","it","my","our",
      "please","pls","need","needs","want","wanted","have","had","get","got","also","again","just","very","really","still","more","some","any","all","none","each","extra","spare","plenty",
      "urgent","asap","quick","quickly","soon","today","tomorrow","now","when","can","could","would","should","might",
      "one","two","three","four","five","six","seven","eight","nine","ten","few","couple",
      "box","boxes","bag","bags","packet","packets","pack","packs","piece","pieces","bottle","bottles","can","cans","tub","tubs","tray","trays","unit","units","carton","cartons","case","cases",
      "if","enough","ok","okay","sure","thanks","thx","ta","cheers","pl"
    ]);
    const set = new Set();
    (stockCatalog||[]).filter(c=>c && c.name && c.status!=="inactive").forEach(c => {
      norm(c.name).split(" ").filter(Boolean).map(stem).forEach(w => {
        if (w && w.length >= 3 && !STOP.has(w)) set.add(w);
      });
      // Also include codeName tokens so "ginger" / "wasabi" code aliases count.
      if (c.codeName) {
        norm(c.codeName).split(" ").filter(Boolean).map(stem).forEach(w => {
          if (w && w.length >= 3 && !STOP.has(w)) set.add(w);
        });
      }
    });
    return set;
  }, [stockCatalog]);
  const findCatalogMatches = (typed, max) => {
    max = max || 8;
    const norm = s => String(s||"").toLowerCase().replace(/[^a-z0-9 ]+/g," ").replace(/\s+/g," ").trim();
    const stem = w => (w && w.length>3 && w.endsWith("s")) ? w.slice(0,-1) : w;
    const tokens = s => norm(s).split(" ").filter(Boolean).map(stem);
    const isSubseq = (needle, haystack) => {
      if (!needle || !haystack) return false;
      let j = 0;
      for (let i = 0; i < haystack.length && j < needle.length; i++) {
        if (haystack[i] === needle[j]) j++;
      }
      return j === needle.length;
    };
    const STOP_WORDS = new Set([
      "the","a","an","of","for","in","on","at","by","with","to","from","and","or","is","are","be","not","this","that","i","we","you","they","it","my","our",
      "please","pls","need","needs","want","wanted","have","had","get","got","also","again","just","very","really","still","more","some","any","all","none","each","extra","spare","plenty",
      "urgent","asap","quick","quickly","soon","today","tomorrow","now","when","can","could","would","should","might",
      "one","two","three","four","five","six","seven","eight","nine","ten","few","couple",
      "box","boxes","bag","bags","packet","packets","pack","packs","piece","pieces","bottle","bottles","can","cans","tub","tubs","tray","trays","unit","units","carton","cartons","case","cases",
      "if","enough","ok","okay","sure","thanks","thx","ta","cheers",
      "pl"
    ]);
    const isDistinctive = (w) => !!w && w.length >= 3 && !STOP_WORDS.has(w);
    const t = norm(typed);
    if(!t) return [];
    const tTokens = tokens(typed);
    const tTokenSet = new Set(tTokens);
    // Off-catalogue distinctive tokens in the typed text — if any are
    // present, the user is naming something the catalogue doesn't cover.
    // Only allow matches with a fullSubMatch or codeMatch (high-confidence
    // user typed the SKU explicitly). Stops "Slushie machine" → Slushie Mix.
    let typedOffCatalogCount = 0;
    for (const w of tTokens) {
      if (w && w.length >= 3 && !STOP_WORDS.has(w) && !catalogDistinctiveTokens.has(w)) {
        typedOffCatalogCount++;
      }
    }
    const items = (stockCatalog||[]).filter(c=>c && c.name && c.status!=="inactive");
    const scored = [];
    for(const c of items){
      const cName = norm(c.name);
      const cTokens = tokens(c.name);
      const cTokenSet = new Set(cTokens);
      if(!cTokens.length) continue;
      let cHits = 0; for(const w of cTokens){ if(tTokenSet.has(w)) cHits++; }
      let tHits = 0; for(const w of tTokens){ if(cTokenSet.has(w)) tHits++; }
      const catCoverage = cHits / cTokens.length;
      const typedCoverage = tTokens.length ? tHits / tTokens.length : 0;
      const fullSubMatch = cName && t.indexOf(cName) !== -1;
      const reverseSubMatch = cName && cName.indexOf(t) !== -1;
      const subseqMatch = !reverseSubMatch && t.length >= 3 && isSubseq(t, cName);
      const codeName = norm(c.codeName);
      const codeMatch = codeName && codeName.length >= 3 && (t.indexOf(codeName) !== -1 || codeName.indexOf(t) !== -1);
      let distinctiveHits = 0;
      for (const w of cTokens) { if (isDistinctive(w) && tTokenSet.has(w)) distinctiveHits++; }
      let score = typedCoverage * 2 + catCoverage;
      if (fullSubMatch) score += 3;
      if (reverseSubMatch) score += 1.5;
      if (subseqMatch) score += 1;
      if (codeMatch) score += 1;
      if (distinctiveHits) score += 1.5 * distinctiveHits;
      if (score < 1.0) continue;
      // 2026-05-26 (JJ "slushie machine" false positive). Off-catalogue token
      // guard: if the typed text contains a distinctive token that appears
      // in NO catalogue item, only allow a match when the catalogue name
      // is a literal substring of (or shares a codeName with) the typed
      // text. Stops one shared category word ("slushie") from surfacing a
      // different SKU when the user clearly named an off-catalogue item.
      if (typedOffCatalogCount > 0 && !fullSubMatch && !codeMatch) continue;
      scored.push({ item: c, score, typedCoverage, nameLen: (c.name||"").length });
    }
    scored.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if (b.typedCoverage !== a.typedCoverage) return b.typedCoverage - a.typedCoverage;
      return a.nameLen - b.nameLen;
    });
    return scored.slice(0, max).map(x => x.item);
  };

  // Continue from Custom tab → if the typed text clashes with a catalogue
  // SKU, surface the matches and block. Otherwise fall through to the
  // normal capture-photo step.
  const continueCustom = () => {
    const t = customText.trim();
    if (!t) return;
    const matches = findCatalogMatches(t);
    if (matches && matches.length) {
      setCustomBlock({ typed: t, items: matches });
      return;
    }
    // Add the one-off to the cart (custom items log at this site / kitchen).
    addToCart({ kind:"custom", item:t, site: site.key, type:"custom" });
    setCustomText("");
  };

  // Two-step: capture kitchen GPS (stock path only — geofenced server-side)
  // → PIN-gate → upload photo to Drive (uploadDeliveryPhoto) → POST
  // stockDelivered (running-list path) or customDeliveryReceived (custom
  // one-off path). Backend's state walker treats delivered+photoUrl as
  // auto-back-in-stock so the row drops off; custom_inbound surfaces only
  // on admin Stock Activity, never on the running list.
  //
  // 2026-05-26: GPS was missing from the stockDelivered POST so the
  // kitchen geofence gate (added 2026-05-19 on backInStock + storeroom
  // flags + stockDelivered) silently rejected every Confirm Delivery from
  // this tile with OUTSIDE_KITCHEN_GEOFENCE. EXIF gpsLat/gpsLng aren't
  // read by the geofence assert — it reads data.lat / data.lng (device
  // GPS, same shape as the StockRequestReceiver photo-confirm modal).
  // BULK SUBMIT — one photo upload, then ONE receiveInboundBatch POST that
  // appends a row per cart item (server reuses the same per-item handlers:
  // backInStock in kitchen mode / stockDelivered single-site / customDelivery-
  // Received for one-offs). Reads the response — never no-cors — and honours a
  // per-item results[] so a partial failure keeps only the failed lines in the
  // cart to retry. Supabase shadows loop per succeeded item, sharing the one
  // photo. Device GPS (2026-05-26 fix) is forwarded for the kitchen offsite FLAG.
  const runSubmit = (kitchenCoords) => {
    requestPin(async (staffName, staffPin) => {
      setSubmitting(true);
      setError("");
      const primary = photos[0] || {};   // first photo = primary (per-item thumb + geofence meta)
      const homeSite = site.key;
      // Resolve each cart line to its write action + originating site. Stock =
      // backInStock: a supplier drop REPLENISHES the storeroom — clears the item's
      // Running Low / Out of Stock flag and its reorder — and leaves any open till
      // request for the Collect tap (stockDelivered) to close, because a box on
      // the storeroom shelf is not the till having collected it. Custom one-off =
      // customDeliveryReceived (admin Stock Activity only).
      const batch = cart.map(c => ({
        key: c.key,
        item: c.item,
        site: c.site || homeSite,
        kind: c.kind,
        action: c.kind === "custom" ? "customDeliveryReceived" : "backInStock",
        type: c.type || (c.kind === "custom" ? "custom" : "standard"),
        qty: (c.qty == null ? null : c.qty),
        row: c.row || null,
      }));
      // Lane BP (13 Sep 2026): ONE kitchen write-drop gate — Lane BE's
      // STOCK_WRITE_DROP_HOSTS (ships EMPTY). On a dropped host the plan turns off
      // the Drive fallback and the receiveInboundBatch POST and makes the Supabase
      // writes the awaited record (the branch below step 1). Everywhere else this
      // function is today's code, untouched.
      const plan = deliveriesSync.planInboundSubmit({ dropOn: stockSync.stockWriteDropEnabled() });
      try {
        // 1) Upload EVERY photo of the drop (sequential). FAST path first: straight
        //    to the PUBLIC Supabase bucket (sub-second) → a permanent public URL.
        //    FALLBACK: Apps Script -> Drive (~8s, and the /exec endpoint flakes
        //    under load returning a non-JSON page → null; foh_flaky_exec_nonjson_
        //    false_success), retried up to 3x — a retried upload only makes a spare
        //    file (SAFE, unlike a non-idempotent stock write). A real backend error
        //    (too large / bad type) is shown and NOT retried.
        const photoUrls = [];
        for (let pi = 0; pi < photos.length; pi++) {
          const p = photos[pi];
          const pb64 = String(p.dataUrl || "").split(",")[1] || "";
          let gotUrl = null, hardError = null;
          // 1a) FAST — direct to Supabase public bucket. null on any failure.
          try {
            setUploadStatus("Uploading photo " + (pi + 1) + " of " + photos.length + "…");
            gotUrl = await deliveriesSync.uploadDeliveryPhotoFast({ siteKey: homeSite, item: batchLabel, staffPin, base64: pb64, mime: p.mime, capturedAt: p.capturedAt });
          } catch (e) { gotUrl = null; }
          // 1b) FALLBACK — Apps Script -> Drive, retried, if the fast path missed.
          //     (Lane BP: never on a dropped host — Google is not written there.)
          if (!gotUrl && plan.driveFallback) {
            const uploadBody = JSON.stringify({
              action: "uploadDeliveryPhoto",
              site: homeSite,
              item: batchLabel,
              staffName, staffPin,
              base64: pb64, mimeType: p.mime, filename: p.filename,
              capturedAt: p.capturedAt, gpsLat: p.gpsLat, gpsLng: p.gpsLng, exifPresent: p.exifPresent, source: p.source
            });
            for (let attempt = 1; attempt <= 3 && !gotUrl && !hardError; attempt++) {
              setUploadStatus("Uploading photo " + (pi + 1) + " of " + photos.length + (attempt > 1 ? " · retry " + (attempt - 1) : "") + " (slow)…");
              let uploadData = null;
              try {
                const uploadRes = await fetch(backend, { method:"POST", headers:{"Content-Type":"text/plain;charset=utf-8"}, body: uploadBody });
                uploadData = await uploadRes.json().catch(()=>null);
              } catch (netErr) { uploadData = null; }
              if (uploadData && uploadData.ok && uploadData.url) { gotUrl = uploadData.url; }
              else if (uploadData && (uploadData.error || uploadData.message)) { hardError = uploadData.error || uploadData.message; }  // deterministic — don't retry
              else if (attempt < 3) { await new Promise(res => setTimeout(res, 900 * attempt)); }   // flaky — back off + retry
            }
          }
          if (!gotUrl) {
            setUploadStatus("");
            setError(hardError || (plan.driveFallback
              ? ("Photo " + (pi + 1) + " didn't upload after 3 tries — the connection looks flaky. Tap Log again.")
              : ("Photo " + (pi + 1) + " didn't upload to the photo store — check the connection and tap Log again.")));
            setSubmitting(false);
            return;
          }
          photoUrls.push(gotUrl);
        }
        setUploadStatus("Saving delivery…");
        const photoUrl = photoUrls[0];   // primary — the per-item row + admin thumbnail
        if (plan.supabaseAuthoritative) {
          // ===== Lane BP ON path (this host is in STOCK_WRITE_DROP_HOSTS): Supabase IS the record. =====
          // No receiveInboundBatch POST to Google. Per cart line, in parallel (one
          // login for the burst): (a) the stock event through postKitchenAction — on
          // a dropped host that is Lane BE's AWAITED outbox landing, carrying the
          // photo's Storage URL, the qty and (for a geofenced action) the device fix
          // the database turns into the offsite flag (1300); it also fires the
          // shadow + reorder signal exactly once, so no separate shadowKitchenWrite
          // here; (b) the evidence row + private photo copy through
          // recordInboundDelivery, AWAITED with strictPhoto. Either failing keeps
          // that line in the cart with the same per-item retry UI a Sheet reject
          // gets, and the message is the kitchen's usual one.
          const todayStr = new Date().toLocaleDateString("en-GB");
          const primaryB64 = String(primary.dataUrl||"").split(",")[1]||"";
          const outcomes = await Promise.all(batch.map(async (b) => {
            const isGeofenced = KITCHEN_GEOFENCED_ACTIONS.indexOf(b.action) !== -1;
            const body = {
              action: b.action, site: b.site, date: todayStr, item: b.item, staffName, staffPin,
              photoUrl, note, qty: b.qty,
              capturedAt: primary.capturedAt, gpsLat: primary.gpsLat, gpsLng: primary.gpsLng, exifPresent: primary.exifPresent, source: primary.source
            };
            if (isGeofenced && kitchenCoords) { body.lat = kitchenCoords.lat; body.lng = kitchenCoords.lng; body.accuracy = kitchenCoords.accuracy; }
            let res;
            try { res = await postKitchenAction(backend, body, isGeofenced); }
            catch (e) { res = { ok: false, code: "supabase", message: (e && e.message) || "That didn't save — try again." }; }
            if (!res || res.ok === false) return { key: b.key, ok: false, message: kitchenActionErrorMessage(res || { ok: false }, b.item) };
            let ev;
            try {
              // Review fix (13 Sep 2026): the evidence row keys on a REAL sites row. In
              // kitchen mode a one-off carries the literal "kitchen" (there is no such
              // site — the kitchen is `mcr`), which postKitchenAction already maps to
              // SITE.key; without the same mapping here siteIdForKey("kitchen") is null
              // and every kitchen one-off failed "evidence didn't save" for ever.
              const evidenceSite = (b.site && b.site !== "kitchen") ? b.site : site.key;
              // Lane CF (14 Sep 2026, BP-7): qty was missing from THIS call entirely —
              // the body above already carries it (line ~5604) for the stock event, but
              // the evidence row never got it, so the ON path (the one that actually
              // runs once a host is armed) would have shipped with the same qty gap the
              // OFF-path shadow call below already avoided.
              ev = await deliveriesSync.recordInboundDelivery({ siteKey: evidenceSite, action: b.action, item: b.item, kind: b.kind, staffPin, note, photoUrl, base64: primaryB64, mime: primary.mime, capturedAt: primary.capturedAt, gpsLat: primary.gpsLat, gpsLng: primary.gpsLng, exifPresent: primary.exifPresent, captureSource: primary.source, qty: b.qty, strictPhoto: true });
            } catch (e) { ev = { ok: false, error: e }; }
            if (!ev || !ev.ok) return { key: b.key, ok: false, message: kitchenActionErrorMessage({ ok: false, code: "supabase", message: "The delivery evidence didn't save — try again." }, b.item) };
            return { key: b.key, ok: true };
          }));
          const failedKeysOn = new Set(outcomes.filter(o => !o.ok).map(o => o.key));
          const succeededOn = batch.filter(b => !failedKeysOn.has(b.key));
          // Optimistic UI — same as the Sheet path: recent strip + prune succeeded stock rows.
          const nowOn = Date.now();
          const newRowsOn = succeededOn.map((b,i) => ({ id:"d-"+nowOn+"-"+i, item:b.item, qty:b.qty, custom:b.kind==="custom", ts:nowOn, staffName, site:b.site, photoUrl, photoUrls, note }));
          setRecentDeliveries(prev => [...newRowsOn, ...prev].slice(0,20));
          const prunedIdsOn = new Set(succeededOn.filter(b => b.kind==="stock" && b.row && b.row.id).map(b => b.row.id+"|"+b.site));
          if (prunedIdsOn.size) {
            setRunningList(prev => {
              const next = prev.filter(x => !prunedIdsOn.has(x.id+"|"+x.site));
              try { localStorage.setItem(RUNNING_LIST_CACHE_KEY, JSON.stringify(next)); } catch (e) {}
              return next;
            });
          }
          if (failedKeysOn.size) {
            setCart(prev => prev.filter(c => failedKeysOn.has(c.key)));
            setStage("build");
            setSubmitting(false);
            const firstMsg = (outcomes.find(o => !o.ok) || {}).message || "";
            const names = batch.filter(b => failedKeysOn.has(b.key)).map(b => b.item).join(", ");
            setError(succeededOn.length + " logged. Couldn't log: " + names + " — " + (firstMsg || "try again."));
            return;
          }
          reset();
          return;
        }
        // Stable idempotency token for this delivery (generate once; survives
        // retries + a manual re-tap; cleared on a clean log in reset()).
        if (!clientTokenRef.current) clientTokenRef.current = "ib-" + Date.now() + "-" + Math.random().toString(36).slice(2, 10);
        // 2) ONE batch write. Read the response so backend rejections surface
        //    (silent_loss_unread_error_body). The write is idempotent per
        //    (clientToken,item) on the backend, so — unlike a bare non-idempotent
        //    retry — a flaky-/exec non-JSON response CAN be retried safely without
        //    double-logging (flaky_exec_causes_duplicate_writes, handled server-side).
        const writeBody = {
          action: "receiveInboundBatch",
          site: homeSite,
          date: new Date().toLocaleDateString("en-GB"),
          staffName, staffPin,
          photoUrl, photoUrls: JSON.stringify(photoUrls), note,
          clientToken: clientTokenRef.current,
          capturedAt: primary.capturedAt, gpsLat: primary.gpsLat, gpsLng: primary.gpsLng, exifPresent: primary.exifPresent, source: primary.source,
          items: JSON.stringify(batch.map(b => ({ key: b.key, item: b.item, site: b.site, action: b.action, type: b.type, qty: b.qty })))
        };
        // Device GPS for the kitchen offsite FLAG (no longer a block, @293).
        if (kitchenCoords) { writeBody.lat = kitchenCoords.lat; writeBody.lng = kitchenCoords.lng; writeBody.accuracy = kitchenCoords.accuracy; }
        const writeBodyStr = JSON.stringify(writeBody);
        // Retry on ANY non-success up to 3x. Because the write is idempotent per
        // (clientToken,item), this is SAFE — and it must cover more than non-JSON:
        // a flaky /exec under load can also return a CROSSED response, i.e. a
        // well-formed JSON error meant for a different request (JJ saw a stray
        // "Unknown action" on a POST that hard-codes the action). So we no longer
        // treat a JSON error as deterministic — we keep the last one to show only
        // if all 3 attempts fail.
        let writeData = null, writeLastError = null;
        for (let attempt = 1; attempt <= 3 && !writeData; attempt++) {
          setUploadStatus(attempt > 1 ? "Saving delivery · retry " + (attempt - 1) + "…" : "Saving delivery…");
          let d = null;
          try {
            const writeRes = await fetch(backend, { method:"POST", headers:{"Content-Type":"text/plain;charset=utf-8"}, body: writeBodyStr });
            d = await writeRes.json().catch(()=>null);
          } catch (e) { d = null; }
          if (d && (d.status === "success" || d.status === "ok")) { writeData = d; break; }
          if (d && (d.message || d.error)) writeLastError = d.message || d.error;
          if (attempt < 3) { await new Promise(res => setTimeout(res, 900 * attempt)); }
        }
        if (!writeData) {
          setUploadStatus("");
          setError((writeLastError ? writeLastError + " — " : "") + "delivery didn't save after 3 tries. Tap Log again — it's safe, it won't double‑log.");
          setSubmitting(false);
          return;
        }
        // Per-item outcomes: the batch handler returns results[]; treat all as
        // logged if it's absent (older backend / all-ok), and keep only the
        // failures in the cart to retry.
        const failedKeys = new Set();
        if (Array.isArray(writeData.results)) {
          // Match results by the STABLE cart key — item name alone is ambiguous
          // (the same SKU can appear for tc AND kitchen in kitchen mode, so two
          // cart lines share a name). Fall back to name only if the backend
          // didn't echo a key (older backend); the current backend always does.
          const byKey = new Map(), byName = new Map();
          writeData.results.forEach(r => {
            if (!r) return;
            if (r.key != null) byKey.set(String(r.key), r);
            else byName.set(String(r.item||"").toLowerCase(), r);
          });
          batch.forEach(b => {
            const r = byKey.get(String(b.key)) || byName.get(String(b.item).toLowerCase());
            if (r && r.ok === false) failedKeys.add(b.key);
          });
        }
        const succeeded = batch.filter(b => !failedKeys.has(b.key));
        // 3) Supabase shadows — per succeeded item, sharing the one photo. Best-effort.
        succeeded.forEach(b => {
          try { shadowKitchenWrite({ action: b.action, site: b.site, item: b.item, staffPin, note, photoPath: photoUrl, qty: b.qty }); } catch (_) {}
          try { deliveriesSync.recordInboundDelivery({ siteKey: b.site, action: b.action, item: b.item, kind: b.kind, staffPin, note, photoUrl, photoUrls, base64: String(primary.dataUrl||"").split(",")[1]||"", mime: primary.mime, capturedAt: primary.capturedAt, gpsLat: primary.gpsLat, gpsLng: primary.gpsLng, exifPresent: primary.exifPresent, captureSource: primary.source, qty: b.qty }).catch(()=>{}); } catch (_) {}
        });
        // 4) Optimistic UI — recent strip + prune succeeded stock rows from the list.
        const now = Date.now();
        const newRows = succeeded.map((b,i) => ({ id:"d-"+now+"-"+i, item:b.item, qty:b.qty, custom:b.kind==="custom", ts:now, staffName, site:b.site, photoUrl, photoUrls, note }));
        setRecentDeliveries(prev => [...newRows, ...prev].slice(0,20));
        const prunedIds = new Set(succeeded.filter(b => b.kind==="stock" && b.row && b.row.id).map(b => b.row.id+"|"+b.site));
        if (prunedIds.size) {
          setRunningList(prev => {
            const next = prev.filter(x => !prunedIds.has(x.id+"|"+x.site));
            try { localStorage.setItem(RUNNING_LIST_CACHE_KEY, JSON.stringify(next)); } catch (e) {}
            return next;
          });
        }
        if (failedKeys.size) {
          setCart(prev => prev.filter(c => failedKeys.has(c.key)));
          setStage("build");
          setSubmitting(false);
          const names = batch.filter(b => failedKeys.has(b.key)).map(b => b.item).join(", ");
          setError(succeeded.length + " logged. Couldn't log: " + names + " — try again.");
          return;
        }
        reset();
      } catch (e) {
        setError(e.message || "Network error — try again");
        setSubmitting(false);
      }
    });
  };

  const submit = () => {
    if (!canSubmit) return;
    // Own premises: no device-GPS capture. The kitchen geofence (the closed
    // kitchen's centroid, flag-not-block) would mark every Trafford drop offsite
    // until the Script Property is cleared, and an indoor mall unit has no usable
    // GPS anyway — the tablet never asks for location and no lat/lng is sent.
    runSubmit(null);
  };

  const fmtTs = (ms) => {
    try {
      const d = new Date(ms);
      const today = new Date();
      const isSame = d.toDateString() === today.toDateString();
      const time = d.toLocaleTimeString("en-GB", {hour:"2-digit", minute:"2-digit"});
      return isSame ? "Today " + time : d.toLocaleDateString("en-GB", {day:"2-digit", month:"short"}) + " · " + time;
    } catch (e) { return ""; }
  };

  const SITE_LABEL = {tc:"Trafford", arndale:"Arndale", meadowhall:"Meadowhall", kitchen:"Kitchen", "(custom)":"Custom"};

  // ===== STAGE 1: pick a target =====
  if (stage === "build") {
    return (
      <div style={S.content}>
        <div style={S.sectionCard}>
          <div style={S.secLabel}>🚛 Inbound Deliveries</div>
          <div style={S.secDesc}>What just arrived? Tick everything from the running list, or add one-off customs — as many as you like. One photo covers the whole delivery.</div>
        </div>
        {/* A partial failure sends the failed lines back here — say so (the
            review screen's banner is off-screen by then). */}
        {error && <div style={{padding:"10px 12px",borderRadius:8,background:"rgba(255,122,92,0.18)",color:"#FF9B83",fontSize:13,fontWeight:600,marginTop:8}}>{error}</div>}

        {/* Tab strip */}
        <div style={{display:"flex",gap:0,marginTop:8,padding:4,background:"rgba(254,250,240,0.04)",borderRadius:10,border:"1px solid rgba(212,251,111,0.08)"}}>
          {["running","custom"].map(k => (
            <button key={k} type="button" onClick={()=>setTab(k)} style={{
              flex:1,padding:"10px 12px",borderRadius:8,border:"none",
              background: tab===k ? B.wasabi : "transparent",
              color: tab===k ? B.nori : B.kewpie,
              fontFamily:"inherit", fontWeight:tab===k?800:600, fontSize:"0.86rem",
              letterSpacing:"0.04em", cursor:"pointer", textTransform:"uppercase"
            }}>{k==="running"?"📋 From running list":"✨ Custom item"}</button>
          ))}
        </div>

        {tab === "running" && (
          <div style={{marginTop:12}}>
            <input style={{...S.input,marginBottom:10,paddingLeft:36,backgroundImage:"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23D4FB6F' opacity='0.4' viewBox='0 0 16 16'%3E%3Cpath d='M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85zm-5.242.156a5 5 0 1 1 0-10 5 5 0 0 1 0 10z'/%3E%3C/svg%3E\")",backgroundRepeat:"no-repeat",backgroundPosition:"12px center",backgroundSize:"14px"}} placeholder="Search item or requester..." value={search} onChange={e=>setSearch(e.target.value)}/>
            {loadingList && <div style={{padding:"24px 0",textAlign:"center",color:B.kewpie,opacity:0.5,fontSize:"0.85rem"}}>Loading running list…</div>}
            {listErr && <div style={{padding:"10px 12px",borderRadius:8,background:"rgba(255,122,92,0.18)",color:"#FF9B83",fontSize:13,fontWeight:600,marginTop:8}}>{listErr}</div>}
            {!loadingList && !listErr && filteredList.length === 0 && (
              <div style={{padding:"24px 16px",textAlign:"center",color:B.kewpie,opacity:0.45,fontSize:"0.85rem",borderRadius:10,background:"rgba(254,250,240,0.03)",border:"1px dashed rgba(254,250,240,0.1)",marginTop:8}}>
                {search.trim() ? <>No items on the list match <em>"{search.trim()}"</em>. Switch to <strong>Custom item</strong> for one-off arrivals.</>
                              : <>Nothing's on order or flagged low right now. If a one-off delivery arrived that wasn't on the list, switch to <strong>Custom item</strong>.</>}
              </div>
            )}
            <div style={{display:"flex",flexDirection:"column",gap:6,marginTop:8}}>
              {filteredList.map(it => {
                const flagPill = it.flag === "out_of_stock" ? {label:"⚠ OOS", bg:"rgba(255,122,92,0.18)", fg:"#FF9B83"}
                              : it.flag === "running_low" ? {label:"⚠ LOW", bg:"rgba(241,197,68,0.22)", fg:"#F1C544"}
                              : null;
                const cat = (stockCatalog||[]).find(c => c && c.name === it.item);
                const img = cat && cat.imageUrl;
                const added = inCart("stock", it.item, it.site);
                return (
                  <button key={(it.id||"x")+"-"+it.site+"-"+it.item} onClick={()=> added
                      ? removeFromCart(cartKey("stock", it.item, it.site))
                      : addToCart({kind:"stock", item:it.item, site:it.site, type: it.custom?"custom":"standard", row:it})} style={{
                    width:"100%",textAlign:"left",fontFamily:"inherit",
                    display:"flex",alignItems:"center",gap:12,
                    padding:"12px 14px",borderRadius:12,
                    background: added ? "rgba(212,251,111,0.14)" : "rgba(212,251,111,0.04)",
                    border:"1px solid " + (added ? "rgba(212,251,111,0.5)" : "rgba(212,251,111,0.12)"),
                    cursor:"pointer", color:B.kewpie
                  }}>
                    <CatalogThumb item={it.item} imageUrl={img} size={52} onZoom={setThumbZoom} showPlaceholder/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:"0.92rem",display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                        <span>{it.item}</span>
                        {it.custom && <span style={{color:B.sunshine,fontSize:"0.65rem",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em"}}>extra</span>}
                        {flagPill && <span style={{background:flagPill.bg,color:flagPill.fg,padding:"2px 8px",borderRadius:6,fontSize:"0.66rem",fontWeight:700,letterSpacing:"0.05em"}}>{flagPill.label}</span>}
                        {it.ordered && <span style={{background:"rgba(212,251,111,0.18)",color:B.wasabi,padding:"2px 8px",borderRadius:6,fontSize:"0.66rem",fontWeight:700,letterSpacing:"0.05em"}}>📦 ON ORDER</span>}
                      </div>
                      <div style={{fontSize:"0.72rem",color:B.kewpie,opacity:0.55,marginTop:2}}>
                        {SITE_LABEL[it.site]||it.site}
                        {it.requestedBy && <> · raised by {it.requestedBy}</>}
                        {it.time && <> · {it.time}</>}
                      </div>
                    </div>
                    <div style={{fontSize:"0.82rem",fontWeight:800,color: added ? B.wasabi : B.kewpie, opacity: added ? 1 : 0.45, flexShrink:0}}>{added ? "✓ Added" : "+ Add"}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {tab === "custom" && (
          <div style={{marginTop:12}}>
            <div style={{padding:"12px 14px",borderRadius:10,background:"rgba(212,251,111,0.06)",border:"1px solid rgba(212,251,111,0.18)",fontSize:"0.78rem",color:B.kewpie,marginBottom:10,lineHeight:1.5}}>
              For one-off arrivals only — equipment, signage, a delivery for someone else, anything not in the catalogue. <strong>If it's a catalogue SKU (sauces, packaging, drinks, snacks, etc.) use the running list.</strong>
            </div>
            <label style={{display:"block",fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.05em",textTransform:"uppercase",color:B.kewpie,opacity:0.6,marginBottom:6}}>What arrived?</label>
            <input value={customText} onChange={e=>setCustomText(e.target.value.slice(0,200))} placeholder="e.g. Slushie machine, marketing flyers, replacement door key" style={{...S.input,width:"100%"}}/>
            {customText.trim() && (
              <button type="button" onClick={continueCustom} style={{...S.submitBtn, marginTop:10, width:"100%"}}>
                + Add "{customText.trim().length>40 ? customText.trim().slice(0,40)+"…" : customText.trim()}"
              </button>
            )}
          </div>
        )}

        {/* In this delivery — the cart. One photo covers all of these. */}
        {cart.length > 0 && (
          <div style={{marginTop:16, padding:14, borderRadius:14, background:"rgba(212,251,111,0.06)", border:"1px solid rgba(212,251,111,0.22)"}}>
            <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",gap:8,marginBottom:10}}>
              <div style={{fontSize:"0.72rem",fontWeight:800,letterSpacing:"0.06em",textTransform:"uppercase",color:B.wasabi}}>In this delivery · {cart.length}</div>
              <div style={{fontSize:"0.68rem",color:B.kewpie,opacity:0.5}}>counts optional</div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {cart.map(c => {
                // Show the catalogue thumbnail so staff can eyeball what each
                // line is (customs have no image → CatalogThumb placeholder).
                const cat = (stockCatalog||[]).find(x => x && x.name === c.item);
                const img = cat && cat.imageUrl;
                return (
                <div key={c.key} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 11px",borderRadius:10,background:"rgba(0,0,0,0.14)",border:"1px solid rgba(212,251,111,0.1)"}}>
                  <CatalogThumb item={c.item} imageUrl={img} size={44} onZoom={setThumbZoom} showPlaceholder/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:"0.86rem",color:B.kewpie,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.item}</div>
                    <div style={{fontSize:"0.66rem",color:B.kewpie,opacity:0.5,marginTop:1}}>{c.kind==="custom" ? "Custom one-off" : (SITE_LABEL[c.site]||c.site)}</div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",border:"1px solid rgba(212,251,111,0.3)",borderRadius:9,overflow:"hidden",flexShrink:0}}>
                    <button type="button" onClick={()=>bumpQty(c.key,-1)} disabled={c.qty<=1} aria-label="less" style={{width:34,height:34,border:"none",background:"rgba(212,251,111,0.08)",color:c.qty<=1?"rgba(212,251,111,0.3)":B.wasabi,fontSize:"1.15rem",fontWeight:800,cursor:c.qty<=1?"default":"pointer",lineHeight:1}}>–</button>
                    <span style={{minWidth:36,textAlign:"center",fontWeight:800,color:B.kewpie,fontSize:"0.92rem",fontVariantNumeric:"tabular-nums"}}>{c.qty}</span>
                    <button type="button" onClick={()=>bumpQty(c.key,1)} aria-label="more" style={{width:34,height:34,border:"none",background:"rgba(212,251,111,0.08)",color:B.wasabi,fontSize:"1.05rem",fontWeight:800,cursor:"pointer",lineHeight:1}}>+</button>
                  </div>
                  <button type="button" onClick={()=>removeFromCart(c.key)} aria-label={"Remove "+c.item} style={{width:30,height:30,borderRadius:8,border:"none",background:"transparent",color:B.kewpie,opacity:0.5,fontSize:"1rem",cursor:"pointer",flexShrink:0,lineHeight:1}}>✕</button>
                </div>
                );
              })}
            </div>
            <button type="button" onClick={()=>{setError("");setStage("review");}} style={{...S.submitBtn, width:"100%", marginTop:12, fontSize:"0.95rem"}}>
              📷 Take photo &amp; log {cart.length} item{cart.length===1?"":"s"} →
            </button>
          </div>
        )}

        {/* Recent deliveries history */}
        <div style={{...S.sectionCard, marginTop:18}}>
          <div style={S.secLabel}>Recent deliveries</div>
          <div style={S.secDesc}>{recentDeliveries.length} log{recentDeliveries.length===1?"":"s"} in the last 7 days. Tap a photo to view full size.</div>
        </div>
        {recentDeliveries.length === 0 ? (
          <div style={{padding:"24px 16px",textAlign:"center",color:B.kewpie,opacity:0.4,fontSize:"0.82rem"}}>No deliveries logged yet.</div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:6,marginTop:8}}>
            {recentDeliveries.map(d => (
              <div key={d.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",borderRadius:10,background:"rgba(254,250,240,0.04)",border:"1px solid rgba(212,251,111,0.08)"}}>
                {d.photoUrl ? (
                  <button type="button" onClick={()=>setPhotoLightbox(d)} style={{width:46,height:46,padding:0,border:"1px solid rgba(212,251,111,0.18)",borderRadius:8,background:"rgba(0,0,0,0.2)",cursor:"pointer",overflow:"hidden",flexShrink:0}}>
                    <img src={d.photoUrl} alt="Delivery" style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}} onError={e=>{e.target.style.display="none";e.target.parentNode.innerText="📷";}}/>
                  </button>
                ) : (
                  <div style={{width:46,height:46,borderRadius:8,background:"rgba(254,250,240,0.06)",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,opacity:0.4,color:B.kewpie}}>📦</div>
                )}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:"0.86rem",color:B.kewpie,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                    <span style={{overflow:"hidden",textOverflow:"ellipsis"}}>{d.item}</span>
                    {d.qty!=null && <span style={{color:B.wasabi,fontWeight:800}}>×{d.qty}</span>}
                    {d.custom && <span style={{color:B.sunshine,fontSize:"0.62rem",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em"}}>custom</span>}
                  </div>
                  <div style={{fontSize:"0.7rem",color:B.kewpie,opacity:0.5,marginTop:2}}>
                    {fmtTs(d.ts)}{d.staffName?" · "+d.staffName:""}{d.site && d.site !== "(custom)" ? " · " + (SITE_LABEL[d.site]||d.site) : ""}
                  </div>
                  {d.note && <div style={{fontSize:"0.68rem",color:B.kewpie,opacity:0.6,marginTop:3,fontStyle:"italic"}}>"{d.note}"</div>}
                </div>
              </div>
            ))}
          </div>
        )}

        {photoLightbox && (
          <div onClick={()=>setPhotoLightbox(null)} style={{position:"fixed",inset:0,background:"rgba(0,28,22,0.88)",zIndex:400,display:"flex",alignItems:"center",justifyContent:"center",padding:24,backdropFilter:"blur(4px)"}}>
            <div onClick={e=>e.stopPropagation()} style={{maxWidth:720,width:"100%",maxHeight:"calc(100dvh - 48px)",background:"#fff",borderRadius:14,overflow:"hidden",display:"flex",flexDirection:"column",boxShadow:"0 22px 48px rgba(0,0,0,0.5)"}}>
              <div style={{padding:"14px 18px",background:B.nori,color:B.wasabi,display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:11,fontWeight:800,letterSpacing:"0.08em",textTransform:"uppercase",color:"#C8EAD8"}}>📷 Delivery photo</div>
                  <div style={{fontFamily:"gelica,serif",fontSize:18,fontWeight:700,color:B.wasabi,marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{photoLightbox.item}</div>
                </div>
                <button onClick={()=>setPhotoLightbox(null)} style={{width:32,height:32,borderRadius:"50%",background:"rgba(0,0,0,0.3)",color:B.wasabi,border:"none",cursor:"pointer",fontSize:16,fontWeight:700,lineHeight:1}}>×</button>
              </div>
              <div style={{flex:1,overflow:"auto",background:"#0a0a0a",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <img src={photoLightbox.photoUrl} alt="Delivery" style={{maxWidth:"100%",maxHeight:"100%",display:"block"}}/>
              </div>
              <div style={{padding:"12px 18px",borderTop:"1px solid rgba(0,57,44,0.1)",fontSize:13,color:"#0d2820",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                <span style={{fontWeight:600}}>{SITE_LABEL[photoLightbox.site]||photoLightbox.site}</span>
                {photoLightbox.staffName && <span style={{opacity:0.65}}>· confirmed by <strong>{photoLightbox.staffName}</strong></span>}
                <span style={{opacity:0.55}}>· {fmtTs(photoLightbox.ts)}</span>
                {photoLightbox.note && <div style={{flex:"1 0 100%",fontSize:12,color:"rgba(0,57,44,0.7)",fontStyle:"italic",borderLeft:"2px solid rgba(0,57,44,0.18)",paddingLeft:10,marginTop:6}}>"{photoLightbox.note}"</div>}
              </div>
            </div>
          </div>
        )}

        {thumbZoom && <CatalogThumbLightbox item={thumbZoom.item} imageUrl={thumbZoom.imageUrl} onClose={()=>setThumbZoom(null)}/>}

        {/* "Did you mean…" — typed custom text clashed with a catalogue SKU. */}
        {customBlock && (
          <div onClick={()=>setCustomBlock(null)} style={{position:"fixed",inset:0,background:"rgba(0,57,44,0.78)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(4px)"}}>
            <div onClick={e=>e.stopPropagation()} style={{maxWidth:480,width:"100%",maxHeight:"calc(100vh - 32px)",overflowY:"auto",background:B.nori,border:"1px solid rgba(212,251,111,0.25)",borderRadius:14,padding:"22px 22px 18px",color:B.kewpie,boxShadow:"0 18px 44px rgba(0,0,0,0.45)"}}>
              <div style={{fontSize:"0.7rem",fontWeight:800,color:B.sunshine,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>⚠ This is a catalogue item</div>
              <div style={{fontFamily:"gelica,serif",fontSize:"1.25rem",lineHeight:1.25,marginBottom:6,color:B.kewpie}}>Use the running list</div>
              <div style={{fontSize:"0.82rem",lineHeight:1.5,opacity:0.75,marginBottom:14}}>You typed <em>"{customBlock.typed}"</em>. That matches a catalogue SKU — tap one below to find it on the running list. Custom is only for one-off arrivals that aren't in the catalogue.</div>
              <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:14}}>
                {(customBlock.items||[]).map(it=>(
                  <button key={it.name} style={{textAlign:"left",padding:"12px 14px",borderRadius:10,background:"rgba(212,251,111,0.05)",color:B.kewpie,border:"1px solid rgba(212,251,111,0.18)",fontFamily:"inherit",cursor:"pointer",display:"flex",alignItems:"center",gap:10}} onClick={()=>{
                    setTab("running");
                    setSearch(it.name);
                    setCustomText("");
                    setCustomBlock(null);
                  }}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:"0.92rem",color:B.wasabi,whiteSpace:"normal",overflowWrap:"anywhere"}}>{it.name}</div>
                      <div style={{fontSize:"0.7rem",opacity:0.55,marginTop:2}}>{it.category||"Catalogue"}</div>
                    </div>
                    <span style={{fontSize:"0.7rem",fontWeight:700,opacity:0.6}}>Find ›</span>
                  </button>
                ))}
              </div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <button style={{flex:"1 1 110px",padding:"12px 16px",borderRadius:10,background:"transparent",color:B.kewpie,border:"1px solid rgba(254,250,240,0.25)",fontFamily:"inherit",fontWeight:700,fontSize:"0.88rem",cursor:"pointer"}} onClick={()=>setCustomBlock(null)}>Edit my text</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ===== STAGE 2: capture photo + confirm =====
  return (
    <div style={S.content}>
      <div style={S.sectionCard}>
        <div style={S.secLabel}>📷 Photos of the delivery{photos.length>1 ? " · "+photos.length : ""}</div>
        <div style={S.secDesc}>Snap the whole drop — add more than one if it helps (different pallets/boxes). At least one is required; they all attach to this delivery of {cart.length} item{cart.length===1?"":"s"}.</div>
      </div>

      {/* What's being logged — the cart, read-only here */}
      <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:10}}>
        {cart.map(c => (
          <span key={c.key} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"6px 11px",borderRadius:999,background:"rgba(212,251,111,0.1)",border:"1px solid rgba(212,251,111,0.25)",color:B.kewpie,fontSize:"0.78rem",fontWeight:600}}>
            {c.item}{c.qty!=null && <strong style={{color:B.wasabi}}>×{c.qty}</strong>}
          </span>
        ))}
      </div>

      {error && <div style={{padding:"10px 12px",borderRadius:8,background:"rgba(255,122,92,0.18)",color:"#FF9B83",fontSize:13,fontWeight:600,marginTop:10}}>{error}</div>}

      {/* Captured photos — a grid; the first is the PRIMARY (per-item thumb). */}
      {photos.length > 0 && (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))",gap:10,marginTop:12}}>
          {photos.map((p,idx) => (
            <div key={p.id} style={{position:"relative",borderRadius:12,overflow:"hidden",border:"1px solid rgba(212,251,111,0.25)",background:"rgba(0,0,0,0.4)",aspectRatio:"4/3"}}>
              <img src={p.dataUrl} alt={"Delivery photo "+(idx+1)} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
              <button type="button" onClick={()=>removePhoto(p.id)} aria-label={"Remove photo "+(idx+1)} style={{position:"absolute",top:6,right:6,width:26,height:26,borderRadius:"50%",background:"rgba(0,28,22,0.82)",color:B.kewpie,border:"1px solid rgba(212,251,111,0.3)",cursor:"pointer",fontSize:14,fontWeight:700,lineHeight:1}}>×</button>
              <div style={{position:"absolute",bottom:6,left:6,display:"flex",gap:4,flexWrap:"wrap"}}>
                {idx===0 && <span style={{padding:"2px 7px",borderRadius:999,background:B.wasabi,color:B.nori,fontSize:"0.58rem",fontWeight:800,letterSpacing:"0.04em"}}>PRIMARY</span>}
                <span style={{padding:"2px 7px",borderRadius:999,background:"rgba(0,57,44,0.85)",color:p.source==="library"?"#FFD68A":B.wasabi,fontSize:"0.58rem",fontWeight:700}}>{p.source==="camera"?"📷 App":p.source==="library"?"🖼️ Roll":"⚠ No meta"}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add photo(s) — camera-first, plus camera-roll. Kept available so staff
          can keep adding shots. Resetting input.value lets the same file re-fire. */}
      {photos.length < 6 ? (
      <div style={{display:"flex",gap:10,marginTop:12, flexDirection: photos.length ? "row" : "column"}}>
        <label style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:8,borderRadius:14,border:"2px dashed rgba(212,251,111,0.3)",background:"rgba(212,251,111,0.04)",cursor:"pointer",textAlign:"center",color:B.kewpie, ...(photos.length ? {padding:"16px 12px"} : {aspectRatio:"4/3",padding:20})}}>
          <span style={{fontSize: photos.length ? 26 : 48}}>📷</span>
          <div style={{fontWeight:800,fontSize: photos.length ? "0.86rem" : "1rem",color:B.wasabi,letterSpacing:"0.02em"}}>{photos.length ? "Add another photo" : "Take photo of delivery"}</div>
          {!photos.length && <div style={{fontSize:12,opacity:0.65,maxWidth:280,lineHeight:1.4}}>Make sure the items + delivery box are clearly visible. Up to 6MB each — add up to 6.</div>}
          <input type="file" accept="image/*" capture="environment" style={{display:"none"}} onChange={e=>{handleFile(e.target.files && e.target.files[0], "camera"); e.target.value="";}}/>
        </label>
        <label style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"10px 14px",borderRadius:10,cursor:"pointer",color:B.kewpie,opacity:0.7,fontSize:"0.82rem",fontWeight:600}}>
          <span style={{fontSize:18}}>🖼️</span>
          <span>{photos.length ? "From camera roll" : "Or upload from camera roll"}</span>
          <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{handleFile(e.target.files && e.target.files[0], "library"); e.target.value="";}}/>
        </label>
      </div>
      ) : (
        <div style={{marginTop:12,padding:"10px 14px",borderRadius:10,background:"rgba(212,251,111,0.06)",border:"1px solid rgba(212,251,111,0.18)",color:B.kewpie,fontSize:"0.8rem",opacity:0.8,textAlign:"center"}}>Maximum of 6 photos — remove one to add another.</div>
      )}

      {/* Note */}
      <div style={{marginTop:12}}>
        <label style={{display:"block",fontSize:11,fontWeight:700,color:B.kewpie,opacity:0.7,letterSpacing:"0.05em",textTransform:"uppercase",marginBottom:6}}>Note (optional)</label>
        <input value={note} onChange={e=>setNote(e.target.value.slice(0,500))} placeholder="e.g. 2 boxes short — supplier called · or · Driver left at back door, looks fine" style={{...S.input,width:"100%"}}/>
      </div>

      {/* Upload progress — photo uploads are slow (~8s each), so show which one. */}
      {submitting && uploadStatus && (
        <div style={{marginTop:12,padding:"10px 14px",borderRadius:10,background:"rgba(212,251,111,0.08)",border:"1px solid rgba(212,251,111,0.2)",color:B.kewpie,fontSize:"0.85rem",fontWeight:600,display:"flex",alignItems:"center",gap:10}}>
          <span style={{width:14,height:14,border:"2px solid rgba(212,251,111,0.3)",borderTopColor:B.wasabi,borderRadius:"50%",display:"inline-block",animation:"spin 0.8s linear infinite",flexShrink:0}}/>
          {uploadStatus}
        </div>
      )}

      {/* Footer actions */}
      <div style={{display:"flex",gap:10,marginTop:18}}>
        <button onClick={()=>{setError("");setStage("build");}} style={{flex:1,padding:"14px 16px",borderRadius:12,border:"1px solid rgba(254,250,240,0.18)",background:"transparent",color:B.kewpie,fontFamily:"inherit",fontWeight:700,fontSize:"0.88rem",cursor:"pointer",opacity:0.85}}>← Back</button>
        <button onClick={submit} disabled={!canSubmit} style={{flex:2,padding:"14px 16px",borderRadius:12,border:"none",background:canSubmit?B.wasabi:"rgba(212,251,111,0.2)",color:B.nori,fontFamily:"inherit",fontWeight:800,fontSize:"0.9rem",cursor:canSubmit?"pointer":"not-allowed",opacity:canSubmit?1:0.45,letterSpacing:"0.02em"}}>{submitting?"Logging…":canSubmit?("✓ Log "+cart.length+" item"+(cart.length===1?"":"s")):"📷 Photo required"}</button>
      </div>
    </div>
  );
}

export { InboundDeliveries };
